/**
 * Обёртка экрана: загрузка, ошибка и содержимое в одном месте.
 *
 * Без неё каждый экран обрабатывает состояния по-своему, и человек видит
 * то спиннер, то пустоту, то сырой текст ошибки.
 */
import { miniApp } from '@telegram-apps/sdk-react';
import type { ReactNode } from 'react';

import { ApiError } from '@/shared/api/client';
import { Button } from '@/shared/ui/kit/Button';

import styles from './kit/Screen.module.css';

interface ScreenProps {
  isLoading: boolean;
  error: unknown;
  children: ReactNode;
  onRetry?: () => void;
}

function describeError(error: unknown): { header: string; description: string } {
  if (error instanceof ApiError) {
    if (error.isExpiredAuth) {
      return {
        header: 'Сессия устарела',
        description: 'Закройте приложение и откройте его снова кнопкой в боте.',
      };
    }
    if (error.isForbidden) {
      return { header: 'Нет доступа', description: error.detail };
    }
    if (error.isUnauthorized) {
      return { header: 'Не удалось войти', description: 'Откройте приложение через бота.' };
    }
  }
  // Детали внутренних сбоев человеку не показываем: они ничего ему
  // не объясняют, а диагностика есть в логах бэкенда.
  return {
    header: 'Что-то пошло не так',
    description: 'Попробуйте обновить через несколько секунд.',
  };
}

export function Screen({ isLoading, error, children, onRetry }: ScreenProps) {
  if (isLoading) {
    return (
      <div className={styles.center}>
        <span className={styles.spinner} role="status" aria-label="Загрузка" />
      </div>
    );
  }

  if (error) {
    const { header, description } = describeError(error);
    return (
      <div className={styles.center}>
        <h1 className={styles.title}>{header}</h1>
        <p className={styles.text}>{description}</p>
        {/* Подпись initData выдаётся на запуск и внутри приложения не обновляется:
            повтор уйдёт с той же устаревшей подписью. Свежую даст только новый
            запуск — поэтому вместо «Повторить» приложение закрывается. */}
        {error instanceof ApiError && error.isExpiredAuth && miniApp.close.isAvailable() ? (
          <Button variant="quiet" size="s" onClick={() => miniApp.close()}>
            Закрыть
          </Button>
        ) : onRetry ? (
          <Button variant="quiet" size="s" onClick={onRetry}>
            Повторить
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.stack}>{children}</div>
    </div>
  );
}

/** Экран-заглушка: используется вне Telegram и для пустых состояний. */
export function Notice({ header, description }: { header: string; description: string }) {
  return (
    <div className={styles.center}>
      <h1 className={styles.title}>{header}</h1>
      <p className={styles.text}>{description}</p>
    </div>
  );
}
